import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { axiosWrapper } from '@/services/axiosWrapper';
import _log from '@/helpers/log';
import { ProjectMeta } from '@/classes/ProjectMeta';
import { ScenarioMeta } from '@/classes/ScenarioMeta';
import { CountryMeta } from '@/classes/CountryMeta';
import ruleService from '@/services/ruleService';
import rulezProject from '@/helpers/rulesProjectHelper';
import type { Filter } from '@/interfaces/PayloadsType';
import { ObjectOnly } from '@/classes/ObjectOnly';

type Dependencies = {
  http: {
    get: <Type>(url: string, payload: unknown, cancelToken: string) => Promise<{ data: Type }>,
    post: <Type>(url: string, payload: unknown) => Promise<{ data: Type }>
    put: <Type>(url: string, payload: unknown) => Promise<{ data: Type }>
  },
  errorHandler: (error: unknown) => void
}

export const buildStructureModule = ({ http, errorHandler }: Dependencies) => {
  @Module({
    namespaced: true
  })
  class StructureModule extends VuexModule {

    public projects: ProjectMeta[] = [];
    public countriesMap: Map<string, CountryMeta> = new Map();
    public projectsLoaded: boolean = false;
    public projectFilter: Filter = {
      filter: '',
      sorter: ''
    };

    get getProjects(): ProjectMeta[] {
      return Array.from(this.projects);
    }

    get getProjectFilter(): Filter {
      return this.projectFilter;
    }

    get getProjectsAreLoaded(): boolean {
      return this.projectsLoaded;
    }

    get getScenariosList(): ScenarioMeta[] {
      return this.getProjects
        .filter(p => p.scenarios)
        .flatMap(e => e.scenarios!);
    }
    get getCountriesMap(): Map<string, CountryMeta> {
      return this.countriesMap;
    }

    @Action
    public async loadProjects(): Promise<void> {
      this.context.commit('setProjectsAreLoaded', false);
      const { filter, sorter }: Filter = this.context.getters.getProjectFilter;
      const payload = { filter, sort: sorter };
      try {
        const { data: projects } = await http.get<object[] | null>('/project/list', payload, 'loadProjects');
        this.context.commit('setProjectsAreLoaded', true);
        this.context.commit('setProjects', (projects ?? []).map(ProjectMeta.fromDto));
      } catch (error) {
        errorHandler(error);
      }
    }

    @Action
    public saveFilter(payload: Filter) {
      this.context.commit('setFilterProjects', payload);
    }

    @Action
    public async loadRules() {
      await ruleService.initialiseProjectRules();
      ruleService.stockCountriesReferences().then((specifications) => {
        const countries = Object.values(specifications)
          .map((specification) => new CountryMeta({
            code: specification['country.en'],
            alpha3: specification['country.Iso 3166-1 alpha-3'],
            fr: specification['country.fr'],
            en: specification['country.en']
          }));
        const country3refRule = rulezProject.getFromRulesDictionary(['project', 'country3Ref']) as any;
        const mappedCountries: string[] = countries.map((e: any) => e.alpha3);
        country3refRule.withOptions(mappedCountries);
        this.context.commit('setCountriesMap', countries);
      });
    }

    @Action
    public addOrUpdateScenario(projectId: any, scenarioId: any) {
      axiosWrapper
        .putOnApi<ObjectOnly>(`/project/${projectId}/scenario/list`, scenarioId)
        .then(response => {
          // update actual scenarios for the project
        }).catch(_log.logError);
    }

    @Action
    public async createProject(payload: ProjectMeta): Promise<void> {
      try {
        await http.put(`/project/`, payload);
        await this.context.dispatch('loadProjects');
      } catch (error) {
        errorHandler(error);
      }
    }

    @Action
    public async updateProject(payload: { id: string, project: ProjectMeta }): Promise<void> {
      try {
        await http.post(`/project/${payload.id}`, payload.project);
        this.projects
          .filter(({ uuid }) => uuid === payload.id)
          .flatMap(({ scenarios }) => scenarios ?? [])
          .forEach(({ uuid }) => this.context.dispatch('scenario/invalidAlgoResults', uuid, { root: true }));
        await this.context.dispatch('loadProjects');
      } catch (error) {
        errorHandler(error);
      }
    }

    @Action
    public deleteProject(projectId: string) {
      axiosWrapper.deleteFromApi(`/project/${projectId}`)
        .then((res: any) => {
          // handler
          this.context.dispatch('loadProjects');
        }).catch(_log.logError);
    }

    @Mutation
    private setProjects(payload: ProjectMeta[]) {
      this.projects = payload;
    }
    @Mutation
    private setProjectsAreLoaded(bool: boolean) {
      this.projectsLoaded = bool;
    }
    @Mutation
    private setCountriesMap(payload: CountryMeta[]) {
      const countriesMap = new Map(payload.map(c => [c.code || 'unknownCountry', c]));
      this.countriesMap = countriesMap;
    }
    @Mutation
    private setFilterProjects(payload: Filter) {
      this.projectFilter = payload;
    }
  }

  return StructureModule;
};
